import React, { useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';

/** Высота плашки: её же читает раскладка, чтобы не уезжать под баннер. */
export const OFFLINE_BANNER_HEIGHT = 36;

/** Текст плашки: сразу объясняем, почему данные могут быть неполными. */
export const OFFLINE_BANNER_TEXT = 'Нет связи, показаны сохранённые данные';

interface OfflineBannerProps {
    /** Перечитать данные с сервера. */
    onRefresh: () => void;
}

/**
 * Тонкая плашка «нет связи» вместо полноэкранной заглушки.
 *
 * Приложение продолжает работать: показываем уже загруженные данные
 * (и кэш service worker), а сверху висит плашка с кнопкой «Обновить».
 */
export const OfflineBanner: React.FC<OfflineBannerProps> = ({ onRefresh }) => {
    // Фиксированной раскладке (DashboardPage) нужно знать высоту плашки,
    // иначе она окажется под ней.
    useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty('--orion-offline-banner-height', `${OFFLINE_BANNER_HEIGHT}px`);
        return () => {
            root.style.removeProperty('--orion-offline-banner-height');
        };
    }, []);

    return (
        <Box
            role="status"
            aria-live="polite"
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: OFFLINE_BANNER_HEIGHT,
                zIndex: (theme) => theme.zIndex.modal + 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                bgcolor: 'warning.light',
                color: 'warning.contrastText',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
            }}
        >
            <WifiOffIcon sx={{ fontSize: 18 }} />
            <Typography
                variant="caption"
                noWrap
                sx={{ fontSize: '0.8rem', fontWeight: 500, lineHeight: 1.2 }}
            >
                {OFFLINE_BANNER_TEXT}
            </Typography>
            <Button
                size="small"
                onClick={onRefresh}
                sx={{
                    ml: 'auto',
                    minWidth: 0,
                    py: 0,
                    px: 1,
                    color: 'inherit',
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                }}
            >
                Обновить
            </Button>
        </Box>
    );
};
